import type { FormEventHandler, ReactNode } from "react";
import { useNavigation } from "@refinedev/core";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

export interface FormShellProps {
  title: string;
  /** One-line subtitle under the title (expected on every page). */
  subtitle?: string;
  resource: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  isSubmitting?: boolean;
  submitLabel?: string;
  children: ReactNode;
}

/**
 * Standard create/edit form chrome (design-system §4.5): the shared `PageHeader`
 * (title + subtitle) over a single hairline card holding a vertical field stack
 * and a Save / Cancel footer. Cancel navigates back to the resource list; Save
 * is the one primary `default` button.
 */
export function FormShell({
  title,
  subtitle,
  resource,
  onSubmit,
  isSubmitting = false,
  submitLabel = "Save",
  children,
}: FormShellProps) {
  const { list } = useNavigation();
  return (
    <form onSubmit={onSubmit} className="mx-auto max-w-2xl space-y-6">
      <PageHeader title={title} subtitle={subtitle} />
      <Card>
        <CardContent className="space-y-4 pt-6">{children}</CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => list(resource)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : submitLabel}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

export default FormShell;
